import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Collapse,
  Avatar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Payment as PayrollIcon,
  Schedule as AttendanceIcon,
  Business as DepartmentIcon,
  EventNote as LeaveIcon,
  Settings as SettingsIcon,
  Person as ProfileIcon,
  Logout as LogoutIcon,
  Assessment as ReportsIcon,
  Security as SecurityIcon,
  CalendarToday as ThirteenthMonthIcon,
  Event as HolidayIcon,
  AccountBalance as BankIcon,
  Receipt as GovernmentIcon,
  Business as BusinessIcon,
  ExpandLess,
  ExpandMore,
  Work as OperationsIcon,
  Group as EmployeeIcon,
  AccountBalance as FinanceIcon,
  AdminPanelSettings as AdminIcon,
  Notifications as NotificationsIcon,
  Analytics as AnalyticsIcon,
  Favorite as FavoriteIcon,
  AccountBalanceWallet as WalletIcon,
  Message as MessageIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  AccountBox as PayrollSystemIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const drawerWidth = 280;

const Sidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut, isAdmin, isEmployee } = useAuth();
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  const handleDarkModeToggle = (checked: boolean) => {
    setDarkMode(checked);
    localStorage.setItem('darkMode', JSON.stringify(checked));
  };
  
  // State for collapsible sections
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    operations: true,
    employees: false,
    finance: false,
    administration: false,
  });

  const handleLogout = async () => {
    try {
      await signOut();
      // Force navigation to login page after logout
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Main navigation items
  const mainNavItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Employees', icon: <PeopleIcon />, path: '/employees' },
    { text: 'Departments', icon: <DepartmentIcon />, path: '/departments' },
    { text: 'Attendance', icon: <AttendanceIcon />, path: '/attendance' },
    { text: 'Payroll', icon: <PayrollIcon />, path: '/payroll' },
    { text: 'Leave Requests', icon: <LeaveIcon />, path: '/leave-requests' },
    { text: 'Government Deductions', icon: <GovernmentIcon />, path: '/government-deductions' },
    { text: 'Bank Payroll', icon: <BankIcon />, path: '/bank-payroll' },
    { text: '13th Month Report', icon: <ThirteenthMonthIcon />, path: '/thirteenth-month-report' },
    { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
    { text: 'Company Settings', icon: <BusinessIcon />, path: '/company-settings' },
    { text: 'Admin Settings', icon: <SecurityIcon />, path: '/admin-settings' },
  ];

  // Admin menu categories for payroll system
  const adminMenuCategories = [
    {
      id: 'operations',
      title: 'Core Operations',
      icon: <OperationsIcon />,
      items: [
        { text: 'Attendance', icon: <AttendanceIcon />, path: '/attendance' },
        { text: 'Leave Requests', icon: <LeaveIcon />, path: '/leave-requests' },
        { text: 'Holiday Management', icon: <HolidayIcon />, path: '/holiday-management' },
      ]
    },
    {
      id: 'employees',
      title: 'Employee Management',
      icon: <EmployeeIcon />,
      items: [
        { text: 'Employees', icon: <PeopleIcon />, path: '/employees' },
        { text: 'Departments', icon: <DepartmentIcon />, path: '/departments' },
      ]
    },
    {
      id: 'finance',
      title: 'Financial Management',
      icon: <FinanceIcon />,
      items: [
        { text: 'Payroll', icon: <PayrollIcon />, path: '/payroll' },
        { text: 'Government Deductions', icon: <GovernmentIcon />, path: '/government-deductions' },
        { text: 'Bank Payroll', icon: <BankIcon />, path: '/bank-payroll' },
        { text: '13th Month Report', icon: <ThirteenthMonthIcon />, path: '/13th-month-report' },
      ]
    },
    {
      id: 'administration',
      title: 'Administration',
      icon: <AdminIcon />,
      items: [
        ...(isAdmin() ? [{ text: 'Reports', icon: <ReportsIcon />, path: '/reports' }] : []),
        { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
        ...(isAdmin() ? [
          { text: 'Admin Settings', icon: <SecurityIcon />, path: '/admin-settings' },
          { text: 'Company Settings', icon: <BusinessIcon />, path: '/company-settings' }
        ] : []),
      ]
    }
  ];

  // Employee menu items (restricted access)
  const employeeMenuItems = [
    { text: 'Attendance', icon: <AttendanceIcon />, path: '/attendance' },
    { text: 'My Payslips', icon: <PayrollIcon />, path: '/my-payslips' },
    { text: 'Profile', icon: <ProfileIcon />, path: '/profile' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const menuCategories = isEmployee() ? [] : adminMenuCategories;

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: darkMode ? '#2d3748' : '#ffffff',
          color: darkMode ? '#ffffff' : '#1a202c',
          borderRight: `1px solid ${darkMode ? '#4a5568' : '#e2e8f0'}`,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease',
        },
      }}
    >
      {/* Header with Logo */}
      <Box sx={{ 
        p: 3, 
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid ${darkMode ? '#4a5568' : '#e2e8f0'}`,
        backgroundColor: darkMode ? '#1a202c' : '#f7fafc',
      }}>
        <Avatar 
          sx={{ 
            width: 40, 
            height: 40, 
            backgroundColor: '#6366f1',
            mr: 2,
            fontSize: '1.2rem',
            fontWeight: 'bold'
          }}
        >
          <PayrollSystemIcon />
        </Avatar>
        <Box>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 600,
              color: darkMode ? '#ffffff' : '#1a202c',
              fontSize: '1.1rem',
              lineHeight: 1.2,
            }}
          >
            Payroll System
          </Typography>
        </Box>
      </Box>


      {/* Main Navigation */}
      <List sx={{ px: 2, py: 1 }}>
        {mainNavItems.map((item) => (
          <ListItemButton
            key={item.text}
            component={Link}
            to={item.path}
            selected={location.pathname === item.path}
            sx={{
              mb: 0.5,
              py: 1.5,
              transition: 'all 0.2s ease-in-out',
              '&.Mui-selected': {
                backgroundColor: '#6366f1',
                color: '#ffffff',
                '& .MuiListItemIcon-root': {
                  color: '#ffffff',
                },
                '&:hover': {
                  backgroundColor: '#5b5cf6',
                },
              },
              '&:hover': {
                backgroundColor: darkMode ? '#4a5568' : '#f7fafc',
              },
            }}
          >
            <ListItemIcon 
              sx={{ 
                color: darkMode ? '#a0aec0' : '#718096',
                minWidth: 40,
                '& .MuiSvgIcon-root': {
                  fontSize: '1.3rem',
                },
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.text}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  color: 'inherit',
                },
              }}
            />
          </ListItemButton>
        ))}
      </List>
      
      <Box sx={{ flexGrow: 1 }} />
      
      {/* Bottom Section */}
      <Box sx={{ p: 2 }}>
        <Divider sx={{ 
          backgroundColor: darkMode ? '#4a5568' : '#e2e8f0',
          mb: 2 
        }} />
        
        {/* Dark Mode Toggle */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 2,
          px: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {darkMode ? 
              <DarkModeIcon sx={{ 
                color: darkMode ? '#a0aec0' : '#718096', 
                fontSize: '1.2rem', 
                mr: 1 
              }} /> : 
              <LightModeIcon sx={{ 
                color: darkMode ? '#a0aec0' : '#718096', 
                fontSize: '1.2rem', 
                mr: 1 
              }} />
            }
            <Typography 
              variant="body2" 
              sx={{ 
                color: darkMode ? '#a0aec0' : '#718096',
                fontSize: '0.9rem'
              }}
            >
              {darkMode ? 'Dark Mode' : 'Light Mode'}
            </Typography>
          </Box>
          <Switch
            checked={darkMode}
            onChange={(e) => handleDarkModeToggle(e.target.checked)}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#6366f1',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#6366f1',
              },
            }}
          />
        </Box>
        
        {/* Settings and Logout */}
        <ListItemButton
          component={Link}
          to="/settings"
          sx={{
            mb: 1,
            py: 1,
            '&:hover': {
              backgroundColor: darkMode ? '#4a5568' : '#f7fafc',
            },
          }}
        >
          <ListItemIcon sx={{ 
            color: darkMode ? '#a0aec0' : '#718096',
            minWidth: 40 
          }}>
            <SettingsIcon sx={{ fontSize: '1.2rem' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Settings"
            sx={{
              '& .MuiTypography-root': {
                fontWeight: 500,
                fontSize: '0.9rem',
                color: darkMode ? '#ffffff' : '#1a202c',
              },
            }}
          />
        </ListItemButton>
        
        <ListItemButton
          onClick={handleLogout}
          sx={{
            py: 1,
            '&:hover': {
              backgroundColor: darkMode ? '#4a5568' : '#f7fafc',
            },
          }}
        >
          <ListItemIcon sx={{ 
            color: darkMode ? '#a0aec0' : '#718096',
            minWidth: 40 
          }}>
            <LogoutIcon sx={{ fontSize: '1.2rem' }} />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            sx={{
              '& .MuiTypography-root': {
                fontWeight: 500,
                fontSize: '0.9rem',
                color: darkMode ? '#ffffff' : '#1a202c',
              },
            }}
          />
        </ListItemButton>
      </Box>
    </Drawer>
  );
};

export default Sidebar;